import { z } from 'zod';
import { STRATEGY_KEYS } from '../enums';
import { decimalInRange, decimalStringSchema, jsonObjectSchema, uuidSchema } from './common';

/** Entry-strategy configuration (API_DOCS.md — Entry Strategies). */

export const strategyConfigSchema = z.object({
  id: uuidSchema,
  key: z.enum(STRATEGY_KEYS),
  displayName: z.string(),
  isActive: z.boolean(),
  params: jsonObjectSchema,
  /** JSON Schema derived from the strategy's zod paramSchema — renders the dashboard form. */
  paramSchema: jsonObjectSchema.nullable(),
  updatedAt: z.string().datetime(),
});
export type StrategyConfigDto = z.infer<typeof strategyConfigSchema>;

/**
 * News-reaction gates (ARCHITECTURE.md §9.7). Every entry strategy's params carry a
 * `gates` object of this shape; each strategy decides which gates apply to it.
 */
const positiveDecimal = decimalStringSchema.refine(decimalInRange('0', '1000000'), 'must be ≥ 0');

export const rvolGateParamsSchema = z.object({
  enabled: z.boolean(),
  /** Recent closed 1m bars whose volume is measured. */
  windowBars: z.number().int().min(1).max(30),
  /** Preceding 1m bars forming the baseline. */
  lookbackBars: z.number().int().min(5).max(100),
  minRvol: positiveDecimal,
  /** How long Strategy A keeps re-checking on 1m closes before the signal expires. */
  maxWaitSec: z.number().int().min(0).max(900),
});

export const pricedInGateParamsSchema = z.object({
  enabled: z.boolean(),
  /** Reject when price already moved this many 5m ATRs since the news. */
  maxMoveAtr: positiveDecimal,
  maxNewsAgeSec: z.number().int().min(60).max(7200),
  atrPeriod: z.number().int().min(2).max(50),
});

export const btcRegimeGateParamsSchema = z.object({
  enabled: z.boolean(),
  lookbackMin: z.number().int().min(1).max(100),
  /** Reject when BTC fell more than this percentage over the lookback. */
  maxBtcDropPct: positiveDecimal,
});

export const spreadGateParamsSchema = z.object({
  enabled: z.boolean(),
  maxSpreadBps: z.number().int().min(1).max(500),
  maxTickerAgeMs: z.number().int().min(500).max(60_000),
});

export const gatesParamsSchema = z.object({
  rvol: rvolGateParamsSchema,
  priced_in: pricedInGateParamsSchema,
  btc_regime: btcRegimeGateParamsSchema,
  spread: spreadGateParamsSchema,
});
export type GatesParams = z.infer<typeof gatesParamsSchema>;

/** Defaults for a news-driven strategy (A); B and C override `enabled` flags in their seeds. */
export const DEFAULT_GATES: GatesParams = {
  rvol: { enabled: true, windowBars: 3, lookbackBars: 20, minRvol: '2.0', maxWaitSec: 180 },
  priced_in: { enabled: true, maxMoveAtr: '2.0', maxNewsAgeSec: 900, atrPeriod: 14 },
  btc_regime: { enabled: true, lookbackMin: 15, maxBtcDropPct: '1.0' },
  spread: { enabled: true, maxSpreadBps: 15, maxTickerAgeMs: 5000 },
};

export const strategyParamsUpdateSchema = z.object({
  params: jsonObjectSchema,
});
export type StrategyParamsUpdate = z.infer<typeof strategyParamsUpdateSchema>;

export const strategyActivateResponseSchema = z.object({
  key: z.enum(STRATEGY_KEYS),
  isActive: z.boolean(),
});
export type StrategyActivateResponse = z.infer<typeof strategyActivateResponseSchema>;

/**
 * Entry-strategy params (ARCHITECTURE.md §9.7 for `gates`). Fields added after the seed rows
 * carry defaults, so a stored params object from an older migration still parses.
 */
const ratio = decimalStringSchema.refine(decimalInRange('0', '1'), 'must be between 0 and 1');
const signedRatio = decimalStringSchema.refine(decimalInRange('-1', '1'), 'must be between -1 and 1');

export const sentimentFirstParamsSchema = z.object({
  minCompositeScore: signedRatio,
  minCompositeConfidence: ratio,
  /** Across all symbols — a burst of headlines must not turn into a burst of entries. */
  maxSignalsPerHour: z.number().int().min(1).max(100),
  /** No second signal on the same symbol inside this many minutes. */
  cooldownPerSymbolMin: z.number().int().min(0).max(1440).default(30),
  gates: gatesParamsSchema.default(DEFAULT_GATES),
});
export type SentimentFirstParams = z.infer<typeof sentimentFirstParamsSchema>;

export const hybridConfirmationParamsSchema = z.object({
  minCompositeScore: signedRatio,
  minCompositeConfidence: ratio.default('0.5'),
  confirmationWindowMs: z.number().int().min(60_000).max(3_600_000),
  /** Bid share of top-of-book depth required (0.6 = 60% bids). */
  minOrderbookImbalance: ratio,
  /** An order-book reading older than this does not confirm anything. */
  maxOrderbookAgeMs: z.number().int().min(1000).max(300_000).default(60_000),
  cooldownPerSymbolMin: z.number().int().min(0).max(1440).default(30),
  gates: gatesParamsSchema.default(DEFAULT_GATES),
});
export type HybridConfirmationParams = z.infer<typeof hybridConfirmationParamsSchema>;

export const technicalHunterParamsSchema = z.object({
  /** Bars come from the market snapshot, which buffers 1m and 5m. */
  timeframe: z.enum(['1m', '5m']),
  rsiPeriod: z.number().int().min(2).max(50),
  rsiOversold: decimalStringSchema.refine(decimalInRange('1', '50'), 'must be between 1 and 50'),
  /** The oversold dip must have happened within this many bars before the MACD turn. */
  oversoldLookbackBars: z.number().int().min(1).max(20).default(5),
  macdFast: z.number().int().min(2).max(50),
  macdSlow: z.number().int().min(3).max(100),
  macdSignal: z.number().int().min(2).max(50),
  atrPeriod: z.number().int().min(2).max(50),
  /** Volume-profile window; price below its POC adds a small confidence bonus. */
  volumeProfileBars: z.number().int().min(20).max(60).default(48),
  cooldownPerSymbolMin: z.number().int().min(0).max(1440).default(60),
  gates: gatesParamsSchema.default({
    ...DEFAULT_GATES,
    rvol: { ...DEFAULT_GATES.rvol, enabled: false },
    priced_in: { ...DEFAULT_GATES.priced_in, enabled: false },
    btc_regime: { ...DEFAULT_GATES.btc_regime, enabled: false },
    spread: { ...DEFAULT_GATES.spread, enabled: false },
  }),
});
export type TechnicalHunterParams = z.infer<typeof technicalHunterParamsSchema>;

/** Donchian lookbacks of the trend ensemble, in days (Zarattini, Pagani & Barbon 2025). */
export const TREND_LOOKBACKS = [10, 20, 30, 60, 90, 150, 250, 360] as const;

/**
 * Strategy D — Trend Follower, live. Daily; the backtest's `binary` mode: full size once
 * `entrySignal` of the lookbacks agree, no resizing while held, sell below `exitSignal`.
 */
export const trendFollowerLiveParamsSchema = z.object({
  /** Coins that may be held on a day: the most traded, by 30-day median daily volume. */
  universeSize: z.number().int().min(1).max(100).default(20),
  minMedianVolumeUsdt: decimalStringSchema.refine(decimalInRange('0', '100000000000'), 'must be ≥ 0').default('10000000'),
  /** Largest position, in % of equity — also capped by the risk settings' max position. */
  maxCoinPct: decimalStringSchema.refine(decimalInRange('1', '100'), 'must be between 1 and 100').default('20'),
  targetVolPct: decimalStringSchema.refine(decimalInRange('5', '300'), 'must be between 5 and 300').default('60'),
  volLookbackDays: z.number().int().min(10).max(365).default(90),
  btcFilter: z.boolean().default(true),
  entrySignal: decimalStringSchema.refine(decimalInRange('0.01', '1'), 'must be between 0.01 and 1').default('0.5'),
  exitSignal: decimalStringSchema.refine(decimalInRange('0', '1'), 'must be between 0 and 1').default('0.25'),
  /** At most this many coins at once; the strongest signals get the free slots. */
  maxPositions: z.number().int().min(1).max(30).default(5),
  /** When every slot is taken, swap the weakest held coin for a full-strength trend. */
  rotation: z.boolean().default(true),
  rotateMinSignal: decimalStringSchema.refine(decimalInRange('0.01', '1'), 'must be between 0.01 and 1').default('1'),
  rotateBelowSignal: decimalStringSchema.refine(decimalInRange('0', '1'), 'must be between 0 and 1').default('0.5'),
  minHoldDays: z.number().int().min(0).max(60).default(3),
  /**
   * 'continuous' — the backtested best: every day each coin's position follows its signal
   * strength (buying more / selling part), the `maxPositions` strongest held. 'binary' —
   * full size once, hold unchanged, sell at `exitSignal` (with optional rotation).
   */
  mode: z.enum(['continuous', 'binary']).default('continuous'),
  /** continuous: skip resizes smaller than this share of the position, in % — saves fees. */
  rebalanceThresholdPct: decimalStringSchema.refine(decimalInRange('0', '100'), 'must be between 0 and 100').default('25'),
});
export type TrendFollowerLiveParams = z.infer<typeof trendFollowerLiveParamsSchema>;

export const STRATEGY_PARAM_SCHEMAS = {
  sentiment_first: sentimentFirstParamsSchema,
  hybrid_confirmation: hybridConfirmationParamsSchema,
  technical_hunter: technicalHunterParamsSchema,
  trend_follower: trendFollowerLiveParamsSchema,
} as const;
